import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
// Build-once background plate: a neutral render of the AdVolt sedan with a CLEAN
// roof (eye-level, ~10 ft). The rooftop LED unit is drawn synthetically at
// runtime so it has the real 2.96:1 screen (967x327 mm) with a thin bezel, and
// the user's ad fills the screen exactly — image or video, no runtime AI.
import carPlatePoster from '../../../assets/images/car-plate.jpg';

// The LED SCREEN's four corners as a % of the plate frame (TL, TR, BR, BL),
// placed to sit mounted on the car roof.
const SCREEN: Array<[number, number]> = [
    [32.9, 9.8],
    [66.1, 9.0],
    [66.1, 26.9],
    [32.9, 27.7]
];
const PLATE_W = 1400;
const PLATE_H = 880;

// ---- Homography: map a source rectangle onto an arbitrary quad as matrix3d. ----
function adj(m: number[]): number[] {
    return [
        m[4] * m[8] - m[5] * m[7], m[2] * m[7] - m[1] * m[8], m[1] * m[5] - m[2] * m[4],
        m[5] * m[6] - m[3] * m[8], m[0] * m[8] - m[2] * m[6], m[2] * m[3] - m[0] * m[5],
        m[3] * m[7] - m[4] * m[6], m[1] * m[6] - m[0] * m[7], m[0] * m[4] - m[1] * m[3]
    ];
}
function multmm(a: number[], b: number[]): number[] {
    const r = new Array(9);
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            let s = 0;
            for (let k = 0; k < 3; k++) s += a[3 * i + k] * b[3 * k + j];
            r[3 * i + j] = s;
        }
    }
    return r;
}
function multmv(m: number[], v: number[]): number[] {
    return [
        m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
        m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
        m[6] * v[0] + m[7] * v[1] + m[8] * v[2]
    ];
}
function basisToPoints(pts: number[][]): number[] {
    const m = [pts[0][0], pts[1][0], pts[2][0], pts[0][1], pts[1][1], pts[2][1], 1, 1, 1];
    const v = multmv(adj(m), [pts[3][0], pts[3][1], 1]);
    return multmm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
}
// Returns matrix3d(...) mapping the srcW x srcH box onto the dst quad (TL,TR,BR,BL).
function quadMatrix3d(srcW: number, srcH: number, dst: number[][]): string {
    const src = [[0, 0], [srcW, 0], [srcW, srcH], [0, srcH]];
    const s = basisToPoints(src);
    const d = basisToPoints(dst);
    const t = multmm(d, adj(s));
    for (let i = 0; i < 9; i++) t[i] = t[i] / t[8];
    const m = [
        t[0], t[3], 0, t[6],
        t[1], t[4], 0, t[7],
        0, 0, 1, 0,
        t[2], t[5], 0, t[8]
    ];
    return `matrix3d(${m.map(n => Number(n.toFixed(6))).join(',')})`;
}

export interface ILedCarPreviewProps {
    mediaUrl: string | null;
    mediaType: 'IMAGE' | 'VIDEO';
    valid: boolean;
    maxWidth?: number;
}

// Homography source box = the screen at ~2.96:1. Exact px are arbitrary.
const SRC_W = 296;
const SRC_H = 100;

const LedCarPreview: React.FunctionComponent<ILedCarPreviewProps> = ({ mediaUrl, mediaType, valid, maxWidth = 720 }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [w, setW] = useState(0);
    const [reduceMotion, setReduceMotion] = useState(false);
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const update = () => setW(el.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReduceMotion(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    const h = w * PLATE_H / PLATE_W;
    const dst = useMemo(() => SCREEN.map(([px, py]) => [px / 100 * w, py / 100 * h]), [w, h]);
    const matrix = w > 0 ? quadMatrix3d(SRC_W, SRC_H, dst) : undefined;
    const showComposite = !!mediaUrl && !!matrix;
    void valid;

    const mappedBox: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${SRC_W}px`,
        height: `${SRC_H}px`,
        transformOrigin: '0 0',
        transform: matrix
    };

    const adMedia = (extra?: React.CSSProperties) => (
        mediaType === 'VIDEO'
            ? <video src={mediaUrl!} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...extra }} autoPlay muted loop playsInline />
            : <img src={mediaUrl!} alt="Your ad on the car" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...extra }} />
    );

    // Hover magnifies the scene toward the ad/panel; the rounded container clips
    // the overflow so it stays clean. Skipped under reduced-motion.
    const zoomActive = hovered && !reduceMotion;

    return (
        <div
            ref={wrapRef}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'relative',
                width: '100%',
                maxWidth: `${maxWidth}px`,
                margin: '0 auto',
                aspectRatio: `${PLATE_W} / ${PLATE_H}`,
                borderRadius: '14px',
                overflow: 'hidden',
                background: '#05070d',
                boxShadow: '0 12px 30px rgba(2, 6, 23, 0.35)'
            }}
        >
          <div style={{
              position: 'absolute',
              inset: 0,
              transformOrigin: '49.5% 18%',
              transform: zoomActive ? 'scale(1.6)' : 'scale(1)',
              transition: 'transform 0.45s ease'
          }}>
            {/* Still plate (clean roof) — keeps geometry pin-sharp. */}
            <img src={carPlatePoster} alt="AdVolt car" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />

            {/* Ambient scene shimmer (subtle life without a moving backplate). */}
            {!reduceMotion && (
                <div style={{
                    position: 'absolute', inset: 0,
                    background: 'radial-gradient(120% 80% at 80% 30%, rgba(255,180,90,0.08), transparent 60%)',
                    mixBlendMode: 'screen', animation: 'ledShimmer 6s ease-in-out infinite', pointerEvents: 'none'
                }} aria-hidden />
            )}

            {showComposite && (
                <React.Fragment>
                    {/* Color-adaptive bloom behind the panel (glow matches the ad). */}
                    <div style={{ ...mappedBox, filter: 'blur(16px) saturate(1.5) brightness(1.3)', opacity: 0.5, pointerEvents: 'none' }} aria-hidden>
                        {adMedia()}
                    </div>

                    {/* Synthetic rooftop unit: low-profile mount base (sits on the
                        roof) + thin-bezel screen. */}
                    <div style={{ ...mappedBox, overflow: 'visible' }}>
                        <div style={{ position: 'absolute', top: '99%', left: '24%', width: '20%', height: '20px', background: 'linear-gradient(180deg, #1c1d21, #070708)' }} aria-hidden />
                        <div style={{ position: 'absolute', top: '99%', right: '24%', width: '20%', height: '20px', background: 'linear-gradient(180deg, #1c1d21, #070708)' }} aria-hidden />
                        <div style={{ position: 'absolute', top: '99%', left: '14%', right: '14%', height: '13px', background: 'linear-gradient(180deg, #26272c, #0a0a0c)', borderRadius: '0 0 3px 3px', boxShadow: '0 3px 6px rgba(0,0,0,0.5)' }} aria-hidden />
                        <div style={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            background: '#000',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            border: '1.5px solid #0b0b0d',
                            boxShadow: '0 0 0 2px #141519, 0 5px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)'
                        }}>
                            {adMedia({ filter: 'brightness(1.06) contrast(1.03)' })}
                            {/* LED dot-matrix texture */}
                            <div style={{
                                position: 'absolute', inset: 0,
                                backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.28) 40%, transparent 42%)',
                                backgroundSize: '5px 5px', mixBlendMode: 'multiply', opacity: 0.45, pointerEvents: 'none'
                            }} />
                            {/* Glass glare */}
                            <div style={{
                                position: 'absolute', inset: 0,
                                background: 'linear-gradient(120deg, rgba(255,255,255,0.14), transparent 42%)', pointerEvents: 'none'
                            }} />
                            {/* Scanline sweep */}
                            {!reduceMotion && (
                                <div style={{
                                    position: 'absolute', left: 0, right: 0, height: '34%', top: '-34%',
                                    background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.10), transparent)',
                                    animation: 'ledScan 4.5s linear infinite', pointerEvents: 'none'
                                }} />
                            )}
                            {/* Glow pulse */}
                            {!reduceMotion && (
                                <div style={{
                                    position: 'absolute', inset: 0, mixBlendMode: 'screen',
                                    background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.10), transparent 70%)',
                                    animation: 'ledPulse 3.6s ease-in-out infinite', pointerEvents: 'none'
                                }} />
                            )}
                        </div>
                    </div>
                </React.Fragment>
            )}
          </div>

            <style>{`
                @keyframes ledScan { 0% { transform: translateY(0); } 100% { transform: translateY(400%); } }
                @keyframes ledPulse { 0%,100% { opacity: 0.4; } 50% { opacity: 0.8; } }
                @keyframes ledShimmer { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
            `}</style>
        </div>
    );
};

export { LedCarPreview };
